import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { IProductItemToBilling } from "api/interfaces";
import { ITableColumn } from "components/interfaces";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { SendToBillingStore as Store } from "store";
import useTablePagination from "../../../hooks/useTablePagination";
import { lang } from "../../../i18nLang";
import { HeadTable } from "../../UI/Table";
import ItemsTableRow from "./ItemsTableRow";
import MaterialUsedHeadCell from "./MaterialUsedHeadCell";

const useStyles = makeStyles({
  container: { height: "calc(100vh - 446px)", overflowY: "auto" },
});

const MessageRow = ({ message }) => (
  <TableRow>
    <TableCell colSpan={6} align="center">
      <Typography style={{ fontWeight: 300 }}>{message}</Typography>
    </TableCell>
  </TableRow>
);

/** Tabela de 'Seleção de Itens' para enviar a medição para Faturamento. */
const ProductItemsTable = observer(() => {
  const rows = Store.productsItems;
  const classes = useStyles();
  const [rowsFiltered, setRowsFiltered] = useState<IProductItemToBilling[]>([]);

  const { getPaginatedList, goToFirstPage, Pagination } = useTablePagination({
    rowsPerPageOptions: [4, 5, 10],
    rowsCount: rowsFiltered.length,
  });

  useEffect(() => {
    goToFirstPage();
    Store.setProductsSearchTerm("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    setRowsFiltered(filterRows(rows));
    goToFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Store.productsSearchTerm, Store.selectedProductsItemsCount]);

  function filterRows(rows: IProductItemToBilling[]) {
    const searchValue = Store.productsSearchTerm;
    let rowsFiltered = [...rows];

    if (searchValue) {
      rowsFiltered = rows.filter((item) => {
        return (
          (item.description || "")
            .toLowerCase()
            .startsWith(searchValue.toLowerCase()) ||
          String(item.codItem).startsWith(searchValue)
        );
      });
    }

    let rowsSorted = rowsFiltered.sort((a, b) => {
      if (a.selected && b.selected)
        return (a.selectedIndex || 0) - (b.selectedIndex || 0);
      if (a.selected) return -1;
      if (b.selected) return 1;
      return 0;
    });
    return rowsSorted;
  }

  const loading = Store.loadingProducts;
  const noRegisters = !loading && rows.length === 0;
  const emptyResults = !loading && rows.length > 0 && rowsFiltered.length === 0;

  const columns: ITableColumn[] = [
    { dataKey: "checkbox", label: lang("select"), padding: "checkbox" },
    { dataKey: "codItem", label: lang("fields.codItem"), width: 100 },
    { dataKey: "description", label: lang("fields.description") },
    { dataKey: "unit", label: lang("fields.unit"), width: 100 },
    {
      dataKey: "materialUsed",
      label: <MaterialUsedHeadCell />,
      padding: "checkbox",
      width: 155,
    },
    { dataKey: "apportioning", label: lang("fields.apportioning"), width: 460 },
  ];

  return (
    <Grid container className={classes.container}>
      <TableContainer>
        <Table size="medium">
          <HeadTable columns={columns} fontWeight={500} />
          <TableBody>
            {loading && <MessageRow message={`${lang("loading")}...`} />}
            {noRegisters && <MessageRow message={lang("billing.noItems")} />}
            {emptyResults && <MessageRow message={lang("billing.noResults")} />}
            {getPaginatedList(rowsFiltered).map((row) => (
              <ItemsTableRow
                key={row.codeItem}
                rowData={row}
                columns={columns}
                disableInputs={!Store.manualApportioning}
                apportiongTotal={Store.productsItemsApportioningTotal}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination />
    </Grid>
  );
});

export default ProductItemsTable;
